import {
  ChatRole,
  ICharacter,
  IConversationMessage,
  NotificationType,
  resolveGreetingAudioRoute,
  setIsOverlayLogin,
  useAppDispatch,
  useGreetingAudioEvents,
  useTranslation,
} from "@next-platform/client-common-core";
import { CapabilityKey, useApplicationCapabilities } from "@next-platform/client-common-core/infra/application/capabilities";
import { useCallback, useEffect, useRef, useState } from "react";

import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";

// Shared across bubbles so only one greeting preview can play at a time.
let greetingAudio: HTMLAudioElement | null = null;
// Identifies the hook instance that started the current preview. Every ChatBar mounts this hook, so
// only the owning instance may tear the shared element down — otherwise an unrelated bubble unmount
// would stop the greeting bubble's playback.
let greetingAudioOwner: object | null = null;

interface IUseGreetingAudioArgs {
  message?: IConversationMessage;
  character: ICharacter | null;
  chatIndex?: number;
  stopLiveTts: () => void;
}

const useGreetingAudio = ({ message, character, chatIndex, stopLiveTts }: IUseGreetingAudioArgs) => {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const dispatch = useAppDispatch();
  const { hasFeatureFlag: isGreetingPlaybackEnabled } = useFeatureFlag(FEATURE_FLAGS.CUSTOM_VOICE_GREETING_PLAYBACK);
  const ttsCapability = useApplicationCapabilities(CapabilityKey.Tts);
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { emitPlay } = useGreetingAudioEvents();
  const refInstanceToken = useRef({});

  const [stateIsGreetingAudioPlaying, setStateIsGreetingAudioPlaying] = useState(false);
  const [stateIsStaticAudioUnavailable, setStateIsStaticAudioUnavailable] = useState(false);

  const isGreetingMessage = chatIndex === 0 && message?.role === ChatRole.BOT;
  const characterId = character?.id ?? "";
  const characterVoiceId = character?.voice_id ?? null;

  const { route, audioUrl } = resolveGreetingAudioRoute({
    isFeatureEnabled: isGreetingPlaybackEnabled,
    isGreetingMessage: !!isGreetingMessage,
    isTtsAllowed: ttsCapability?.allowed ?? false,
    isPaywall: ttsCapability?.isPaywall ?? false,
    // Kinde reports isAuthenticated=false while loading; do not treat a signed-in user as a guest yet.
    isAuthenticated: isAuthenticated || isAuthLoading,
    hasCharacterVoice: !!characterVoiceId,
    messageGreetingAudioUrl: message?.greeting_audio_url,
  });

  const isStaticRoute = route === "static" && !!audioUrl && !stateIsStaticAudioUnavailable;
  const isSignInRoute = route === "signIn";
  // On a lazy 404 the static source is dropped and the icon is hidden entirely.
  const isGreetingIconHidden = route === "static" && stateIsStaticAudioUnavailable;

  const stopGreetingAudio = useCallback(() => {
    if (greetingAudio) {
      // Detach handlers before teardown so the empty-source abort does not fire onerror and
      // trip the unavailable path. removeAttribute + load empties the element without a failed load.
      greetingAudio.onended = null;
      greetingAudio.onerror = null;
      greetingAudio.pause();
      greetingAudio.removeAttribute("src");
      greetingAudio.load();
    }
    greetingAudioOwner = null;
    setStateIsGreetingAudioPlaying(false);
  }, []);

  const handleStaticUnavailable = useCallback(() => {
    stopGreetingAudio();
    setStateIsStaticAudioUnavailable(true);
    displayToast({ message: t("chat:page.greetingAudio.toast.unavailable"), type: NotificationType.error });
  }, [stopGreetingAudio, displayToast, t]);

  useEffect(() => {
    const instanceToken = refInstanceToken.current;
    return () => {
      // Only the instance that started the preview may tear it down; unrelated bubble unmounts leave
      // the owning bubble's playback and speaker icon intact.
      if (greetingAudioOwner !== instanceToken) {
        return;
      }
      stopGreetingAudio();
    };
  }, [stopGreetingAudio]);

  // Reset the lazy-404 guard whenever the underlying source changes.
  useEffect(() => {
    setStateIsStaticAudioUnavailable(false);
  }, [audioUrl]);

  // Any logged-in user can play the greeting, so a guest is asked to sign in rather than to upgrade.
  const handleGreetingSignIn = useCallback(() => {
    dispatch(setIsOverlayLogin(true));
  }, [dispatch]);

  const handleGreetingStaticPlay = useCallback(async () => {
    if (!audioUrl) {
      return;
    }
    if (stateIsGreetingAudioPlaying) {
      stopGreetingAudio();
      return;
    }

    stopLiveTts();
    if (!greetingAudio) {
      greetingAudio = new Audio();
    }
    const audioElement = greetingAudio;
    audioElement.onended = () => {
      greetingAudioOwner = null;
      setStateIsGreetingAudioPlaying(false);
    };
    audioElement.onerror = () => handleStaticUnavailable();

    try {
      audioElement.src = audioUrl;
      greetingAudioOwner = refInstanceToken.current;
      setStateIsGreetingAudioPlaying(true);
      emitPlay({ characterId, voiceId: characterVoiceId });
      await audioElement.play();
    } catch {
      handleStaticUnavailable();
    }
  }, [audioUrl, stateIsGreetingAudioPlaying, stopGreetingAudio, stopLiveTts, handleStaticUnavailable, emitPlay, characterId, characterVoiceId]);

  return {
    isGreetingStaticRoute: isStaticRoute,
    isGreetingSignInRoute: isSignInRoute,
    isGreetingAudioPlaying: stateIsGreetingAudioPlaying,
    isGreetingIconHidden,
    handleGreetingStaticPlay,
    handleGreetingSignIn,
  };
};

export default useGreetingAudio;
